/** Task data hooks — the §7.6 query contract, shared by every view. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BulkOp, Priority, StatusCategory, TaskType } from '@virtuetasks/types';
import { api, qs } from '@/lib/api';

export interface ChecklistItemView {
  id: string;
  text: string;
  done: boolean;
  order: number;
}

export interface TaskView {
  _id: string;
  key: string;
  projectId: string;
  projectKey: string;
  title: string;
  description?: Record<string, unknown>;
  type: TaskType;
  statusId: string;
  statusCategory: StatusCategory;
  priority: Priority;
  priorityScore: number;
  reporterId: string;
  assigneeIds: string[];
  primaryAssigneeId?: string;
  reviewerId?: string;
  watcherIds?: string[];
  labelIds: string[];
  dueDate?: string;
  startDate?: string;
  completedAt?: string;
  estimateMinutes?: number;
  spentMinutes: number;
  commentCount: number;
  attachmentCount: number;
  subtaskCount: number;
  subtaskDoneCount: number;
  checklist: ChecklistItemView[];
  blockedBy: string[];
  blocks: string[];
  blockedReason?: string;
  parentTaskId?: string;
  sprintId?: string;
  isArchived: boolean;
  rank: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface TaskGroupView {
  groupKey: string;
  groupLabel: string;
  count: number;
  tasks: TaskView[];
}

export type TaskFilters = Record<string, unknown>;

export const tasksKey = (filters: TaskFilters) => ['tasks', filters] as const;

export function useTasks(filters: TaskFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: tasksKey(filters),
    queryFn: async () => {
      const res = await api.get<TaskView[] | TaskGroupView[]>(`/tasks${qs(filters)}`);
      return { items: res.data, meta: res.meta };
    },
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

export function useTask(idOrKey: string | undefined) {
  return useQuery({
    queryKey: ['task', idOrKey],
    queryFn: async () => (await api.get<TaskView>(`/tasks/${idOrKey}`)).data,
    enabled: Boolean(idOrKey),
  });
}

export function useLabels() {
  return useQuery({
    queryKey: ['labels'],
    queryFn: async () =>
      (await api.get<{ _id: string; name: string; colour: string; usageCount: number }[]>('/labels')).data,
  });
}

function useTaskInvalidation() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: ['task'] });
    void qc.invalidateQueries({ queryKey: ['my-day'] });
    // Every one of these mutations writes an activity row, so the timeline
    // beside the task was going stale until something else refreshed it.
    // V4 D-2 made that visible: the done banner names who finished the task,
    // and it read the feed from before the change.
    void qc.invalidateQueries({ queryKey: ['timeline'] });
  };
}

export function useCreateTask() {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      // A UUID per submission means a retry cannot double-create (§5-F4).
      const idempotencyKey = crypto.randomUUID();
      return (await api.post<TaskView>('/tasks', input, { headers: { 'idempotency-key': idempotencyKey } }))
        .data;
    },
    onSuccess: invalidate,
  });
}

export function useQuickAdd() {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: async (input: { text: string; projectId?: string }) =>
      (
        await api.post<TaskView>('/tasks/quick-add', input, {
          headers: { 'idempotency-key': crypto.randomUUID() },
        })
      ).data,
    onSuccess: invalidate,
  });
}

/**
 * `PATCH /tasks/:id` carries the version in If-Match; a 409 means someone else
 * saved first and the UI refetches (§5-F4).
 */
export function useUpdateTask() {
  const invalidate = useTaskInvalidation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      version,
      patch,
    }: {
      id: string;
      version: number;
      patch: Record<string, unknown>;
    }) =>
      (await api.patch<TaskView>(`/tasks/${id}`, patch, { headers: { 'if-match': String(version) } }))
        .data,
    onSuccess: (task) => {
      qc.setQueryData(['task', task._id], task);
      invalidate();
    },
  });
}

/** Sub-endpoints do not need If-Match — they mutate disjoint fields (§5-F4). */
export function useChangeStatus() {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: async ({ id, statusId, reason }: { id: string; statusId: string; reason?: string }) =>
      (await api.patch<TaskView>(`/tasks/${id}/status`, { statusId, ...(reason ? { reason } : {}) })).data,
    onSuccess: invalidate,
  });
}

export function useSetAssignees() {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: async ({ id, assigneeIds }: { id: string; assigneeIds: string[] }) =>
      (await api.patch<TaskView>(`/tasks/${id}/assignees`, { assigneeIds })).data,
    onSuccess: invalidate,
  });
}

export interface BulkResultView {
  updated: string[];
  skipped: { id: string; reason: string }[];
}

export function useBulkTasks() {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: async (input: { taskIds: string[]; op: BulkOp; payload: Record<string, unknown> }) =>
      (await api.post<BulkResultView>('/tasks/bulk', input)).data,
    onSuccess: invalidate,
  });
}

export function useDeleteTask() {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/tasks/${id}`),
    onSuccess: invalidate,
  });
}

/* ── Task detail workbench (V3 R3) ─────────────────────── */

export interface CommentView {
  _id: string;
  entityType: string;
  entityId: string;
  parentCommentId?: string | null;
  authorId: string;
  authorName: string;
  body: Record<string, unknown>;
  bodyText: string;
  mentionedUserIds: string[];
  reactions?: { emoji: string; userIds: string[] }[];
  isDeleted?: boolean;
  /** V6-02: the comment was deleted; body and attachments are already gone. */
  isTombstoned?: boolean;
  tombstonedBy?: string;
  tombstonedByName?: string;
  tombstonedAt?: string;
  /** Server-decided, because a project lead's right to moderate is not in the token. */
  canDelete?: boolean;
  editedAt?: string;
  createdAt: string;
}

export function useComments(entityType: 'task' | 'note', entityId?: string) {
  return useQuery({
    queryKey: ['comments', entityType, entityId],
    enabled: Boolean(entityId),
    queryFn: async () =>
      (await api.get<CommentView[]>(`/comments${qs({ entityType, entityId, limit: 200 })}`)).data,
  });
}

/**
 * Invalidate by *prefix*, never by a specific id.
 *
 * `useTask` is keyed on whatever identifier the caller had — the detail page
 * routes by `key` (`VL-142`), while every mutation only knows the `_id`. An
 * id-scoped invalidation therefore misses the very query the user is looking
 * at: the comment posted, and the thread updated, but the timeline beside it
 * did not.
 */
function useDetailInvalidation() {
  const qc = useQueryClient();
  return () => {
    for (const key of ['comments', 'timeline', 'task', 'tasks', 'subtasks', 'dependencies', 'attachments']) {
      void qc.invalidateQueries({ queryKey: [key] });
    }
  };
}

export function useCreateComment(_entityId?: string) {
  const invalidate = useDetailInvalidation();
  return useMutation({
    mutationFn: async (input: {
      entityType: 'task' | 'note';
      entityId: string;
      body: Record<string, unknown>;
      parentCommentId?: string | null;
    }) => (await api.post<CommentView>('/comments', input)).data,
    onSuccess: invalidate,
  });
}

export function useUpdateComment(_entityId?: string) {
  const invalidate = useDetailInvalidation();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      (await api.patch<CommentView>(`/comments/${id}`, { body })).data,
    onSuccess: invalidate,
  });
}

export function useDeleteComment(_entityId?: string) {
  const invalidate = useDetailInvalidation();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/comments/${id}`),
    onSuccess: invalidate,
  });
}

export function useReact(_entityId?: string) {
  const invalidate = useDetailInvalidation();
  return useMutation({
    mutationFn: async ({ id, emoji, on }: { id: string; emoji: string; on: boolean }) =>
      on
        ? (await api.post<CommentView>(`/comments/${id}/reactions`, { emoji })).data
        : (await api.delete<CommentView>(`/comments/${id}/reactions${qs({ emoji })}`)).data,
    onSuccess: invalidate,
  });
}

export interface AttachmentView {
  _id: string;
  entityType: string;
  entityId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  thumbUrl?: string;
  previewUrl?: string;
  uploadedBy: string;
  uploadStatus: 'pending' | 'complete' | 'failed';
  createdAt: string;
}

/** V4 F-1/F-2: projects and notes carry attachments too, not only tasks. */
export type AttachmentEntity = 'task' | 'note' | 'comment' | 'project';

export function useAttachments(entityType: AttachmentEntity, entityId?: string) {
  return useQuery({
    queryKey: ['attachments', entityType, entityId],
    enabled: Boolean(entityId),
    queryFn: async () =>
      (await api.get<AttachmentView[]>(`/attachments${qs({ entityType, entityId })}`)).data,
  });
}

/**
 * Three-step upload (§F8): presign → PUT the bytes straight to storage →
 * complete. Bytes never pass through the API server, in dev or in production.
 */
export function useUploadAttachment(_entityId?: string) {
  const invalidate = useDetailInvalidation();
  return useMutation({
    mutationFn: async ({
      file,
      entityType,
      entityId: target,
    }: {
      file: File;
      entityType: AttachmentEntity;
      entityId: string;
    }) => {
      const { data: presigned } = await api.post<{
        attachmentId: string;
        uploadUrl: string;
      }>('/attachments/presign', {
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        entityType,
        entityId: target,
      });

      const put = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'content-type': file.type || 'application/octet-stream' },
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);

      return (await api.post<AttachmentView>(`/attachments/${presigned.attachmentId}/complete`, {}))
        .data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteAttachment(_entityId?: string) {
  const invalidate = useDetailInvalidation();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/attachments/${id}`),
    onSuccess: invalidate,
  });
}

export interface TimelineRow {
  type: 'comment' | 'activity';
  at: string;
  payload: Record<string, unknown>;
}

export function useTimeline(idOrKey?: string) {
  return useQuery({
    queryKey: ['timeline', idOrKey],
    enabled: Boolean(idOrKey),
    queryFn: async () => (await api.get<TimelineRow[]>(`/tasks/${idOrKey}/activity`)).data,
  });
}

export function useSubtasks(id?: string) {
  return useQuery({
    queryKey: ['subtasks', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<TaskView[]>(`/tasks/${id}/subtasks`)).data,
  });
}

export interface DependencyView {
  blockedBy: TaskView[];
  blocks: TaskView[];
}

export function useDependencies(id?: string) {
  return useQuery({
    queryKey: ['dependencies', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<DependencyView>(`/tasks/${id}/dependencies`)).data,
  });
}

export function useSetDependency(id: string) {
  const invalidate = useDetailInvalidation();
  return useMutation({
    // The wire field is `type`, and DELETE carries a body rather than a query
    // string — both halves of `dependencySchema` in packages/types.
    mutationFn: async ({
      taskId,
      relation,
      remove,
    }: {
      taskId: string;
      relation: 'blockedBy' | 'blocks';
      remove?: boolean;
    }) =>
      remove
        ? api.delete(`/tasks/${id}/dependencies`, { taskId, type: relation })
        : api.post(`/tasks/${id}/dependencies`, { taskId, type: relation }),
    onSuccess: invalidate,
  });
}

export function useChecklist(id: string) {
  const invalidate = useDetailInvalidation();

  const add = useMutation({
    mutationFn: async (text: string) => (await api.post<TaskView>(`/tasks/${id}/checklist`, { text })).data,
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ itemId, patch }: { itemId: string; patch: { text?: string; done?: boolean } }) =>
      (await api.patch<TaskView>(`/tasks/${id}/checklist/${itemId}`, patch)).data,
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (itemId: string) => api.delete(`/tasks/${id}/checklist/${itemId}`),
    onSuccess: invalidate,
  });
  /** §F4: a checklist item that grew up becomes a subtask, keeping its text. */
  const convert = useMutation({
    mutationFn: async (itemId: string) =>
      (await api.post<TaskView>(`/tasks/${id}/checklist/${itemId}/convert`)).data,
    onSuccess: invalidate,
  });

  return { add, update, remove, convert };
}

export function useWatch(id: string) {
  const invalidate = useDetailInvalidation();
  return useMutation({
    mutationFn: async (watching: boolean) =>
      watching ? api.delete(`/tasks/${id}/watchers`) : api.post(`/tasks/${id}/watchers`),
    onSuccess: invalidate,
  });
}

export function useSuggestedAssignees(params: {
  projectId?: string;
  type?: string;
  estimateMinutes?: number;
  dueDate?: string;
}) {
  return useQuery({
    queryKey: ['suggest-assignees', params],
    enabled: Boolean(params.projectId),
    queryFn: async () =>
      (
        await api.get<{ userId: string; name: string; score: number; reasons: string[] }[]>(
          `/tasks/suggest-assignees${qs(params)}`,
        )
      ).data,
  });
}
